import { showActionsModal, showModal } from './Modal.js';
import { gameState } from '../state/gameState.js';
import { showToast } from './Toast.js';
import { messageFor } from '../api/errors.js';
import { instructorGreeting, menuActions } from './quizLogic.mjs';
import { showQuizModal, showQuizResultModal } from './QuizModal.js';
import { showTheoryLessonsModal } from './TheoryLessonsModal.js';
import { showLicenceModal } from './LicenceModal.js';
import { showLessonBriefing } from './LessonBriefing.js';
import { showControlsCard, hasSeenControls } from './ControlsCard.js';
import { BRIEFING_PL, EXAM_RULES_PL, ROUTE_DEFS } from '../scenes/lessonScript.mjs';
import { SCENE, STREET_SPAWNS } from '../config.js';

const errorCode = (e) => e?.detail?.error_code ?? e?.error_code;

// Start a test, clearing a stale session first if one is still on the server. The opening
// abort below can be lost: /api/state is one JSONB blob written last-write-wins, so a
// set-location POST that overlaps the abort re-saves the pre-abort snapshot. Retrying here
// (where the child is waiting anyway) beats leaving them stuck on "Masz już rozpoczęty test".
//
// Practice only, though: aborting an EXAM counts as a fail and sets the 24 h cooldown, so
// the retry would just bounce off DRIVING_COOLDOWN and the earlier 50 zł would disappear
// with no explanation. For an exam we abort once, say what happened and return null — the
// caller re-renders the menu, where the exam row now shows the cooldown hint.
// Returns the start response, or null when the exam attempt was consumed.
async function startQuiz(scene, kind) {
  try {
    return await gameState.drivingQuizStart(kind);
  } catch (e) {
    if (errorCode(e) !== 'DRIVING_SESSION_ACTIVE') throw e;
    if (kind === 'exam') {
      try { await gameState.drivingQuizAbort(); } catch { /* already gone server-side */ }
      showToast(scene, 'Poprzedni egzamin przepadł — następne podejście jutro {hourglass}');
      return null;
    }
    await gameState.drivingQuizAbort();
    return gameState.drivingQuizStart(kind);
  }
}

// Start a practical lesson/exam, mirroring startQuiz's stale-session recovery: the pre-loop
// abort at menu-open can miss a session created just after it ran (another tab, a slow
// abort overwritten by a concurrent set-location write — same JSONB last-write-wins race
// noted on startQuiz above). On DRIVING_SESSION_ACTIVE, abort whatever practical_session the
// client currently knows about (defaulting to a lesson-abort — starting a lesson only ever
// collides with another lesson) and retry the start once; a second failure propagates to the
// caller's catch, which toasts and re-renders the menu from the refreshed state.
async function startPractical(startFn) {
  try {
    return await startFn();
  } catch (e) {
    if (errorCode(e) !== 'DRIVING_SESSION_ACTIVE') throw e;
    const staleKind = gameState.raw?.driving?.practical_session?.kind ?? 'lesson';
    try {
      if (staleKind === 'exam') await gameState.drivingPracticalAbort();
      else await gameState.drivingLessonAbort();
    } catch { /* already gone server-side */ }
    return startFn();
  }
}

// The instructor's menu. Re-opens after each sub-action so the child can chain
// "lessons → practice test → exam" without walking up to her again; ✕ ends the talk.
export async function showDrivingSchoolMenu(scene) {
  // A session left open (refresh mid-test) can't be resumed — abort it up front.
  if (gameState.raw?.driving?.session) {
    const wasExam = gameState.raw.driving.session.kind === 'exam';
    try { await gameState.drivingQuizAbort(); } catch { /* already expired server-side */ }
    showToast(scene, wasExam
      ? 'Poprzedni egzamin został przerwany i liczy się jako oblany'
      : 'Poprzedni test został przerwany');
  }
  // A practical lesson/exam session left open (refresh mid-drive) can't be resumed either —
  // abort it up front so the menu below reflects fresh state.
  if (gameState.raw?.driving?.practical_session) {
    const kind = gameState.raw.driving.practical_session.kind;
    try {
      if (kind === 'lesson') await gameState.drivingLessonAbort();
      else await gameState.drivingPracticalAbort();
    } catch { /* already expired server-side */ }
    showToast(scene, kind === 'lesson'
      ? 'Poprzednia lekcja została przerwana'
      : 'Poprzedni egzamin przepadł');
  }
  for (;;) {
    const d = gameState.raw.driving, cat = gameState.raw.catalog.driving;
    const rows = menuActions(d, cat);
    const picked = await showActionsModal(scene, {
      title: 'Pani Instruktor',
      body: instructorGreeting(d),
      actions: rows.map((r) => ({
        // Long hints (the retry clock) go on a second line — inline they run off the button.
        label: r.hint ? `${r.label}${r.hint.length > 12 ? '\n' : '  '}(${r.hint})` : r.label,
        disabled: r.disabled,
        fn: async () => r.key,
      })),
    });
    if (!picked || picked.error) return;
    try {
      if (picked === 'course') {
        const ok = await showModal(scene, {
          title: 'Kurs prawa jazdy',
          body: `Zapisać się na kurs za ${cat.course_price} zł?`,
          confirmLabel: 'Zapisz mnie',
        });
        if (ok) { await gameState.drivingBuyCourse(); showToast(scene, 'Witaj na kursie! {note}'); }
      } else if (picked === 'lessons') {
        await showTheoryLessonsModal(scene);
      } else if (picked === 'practice' || picked === 'exam') {
        if (picked === 'exam') {
          const ok = await showModal(scene, {
            title: 'Egzamin teoretyczny',
            body: `${cat.exam_questions} pytań, trzeba ${cat.pass_score} dobrych. Kosztuje ${cat.theory_exam_price} zł. Zaczynamy?`,
            confirmLabel: 'Zaczynamy!',
          });
          if (!ok) continue;
        }
        const res = await startQuiz(scene, picked);
        if (!res) continue;   // exam attempt consumed by a stale session — menu re-renders
        const result = await showQuizModal(scene, { kind: picked, quiz: res.quiz });
        if (result) await showQuizResultModal(scene, result);
      } else if (picked === 'practical_lessons') {
        const n = d.next_lesson;
        const route = cat.routes.find((r) => r.n === n);
        const segments = route?.segments ?? ROUTE_DEFS[`lesson${n}`] ?? [];
        const go = await showLessonBriefing(scene, {
          title: `Lekcja ${n}: ${route?.name_pl ?? ''}`,
          items: BRIEFING_PL(segments, 'lesson'),
          cta: 'Jedziemy! {car}',
        });
        if (!go) continue;
        // First lesson, and the child has never seen the controls reference — show it
        // once, right before the wheel is handed over.
        if (n === 1 && !hasSeenControls()) await showControlsCard(scene);
        await startPractical(() => gameState.drivingLessonStart(n));
        // Like the exam: the Yaris waits on the street in front of the school, and the route
        // begins once you drive off its left edge.
        scene.scene.start(SCENE.OUTSIDE, {
          spawnX: STREET_SPAWNS.DRIVING_SCHOOL,
          lesson: { route: n, kind: 'lesson', lesson: n },
        });
        return;
      } else if (picked === 'practical') {
        const examSegments = cat.exam_route?.segments ?? ROUTE_DEFS.exam;
        const go = await showLessonBriefing(scene, {
          title: 'Egzamin praktyczny',
          items: BRIEFING_PL(examSegments, 'exam'),
          cta: 'Zaczynamy!',
          // What it costs comes straight off the catalog — the same number the menu row
          // shows — so the child sees the price on the last screen before it is charged.
          note: `${EXAM_RULES_PL}\nEgzamin kosztuje ${cat.practical_exam_price} zł`,
        });
        if (!go) continue;
        await startPractical(() => gameState.drivingPracticalStart());
        scene.scene.start(SCENE.OUTSIDE, {
          spawnX: STREET_SPAWNS.DRIVING_SCHOOL,
          lesson: { route: 'exam', kind: 'exam' },
        });
        return;
      } else if (picked === 'controls') {
        await showControlsCard(scene);
      } else if (picked === 'licence') {
        await showLicenceModal(scene);
      }
    } catch (e) {
      showToast(scene, messageFor(e));
    }
  }
}
